import { api } from "../api";
import { shouldShowOnboarding, renderOnboardingChecklist, attachChecklistListeners, OnboardingState } from "../components/onboarding-checklist";
import { Icons } from "../utils/icons";

export async function render(el: HTMLElement) {
  el.innerHTML = `<div class="page-header"><h1>Dashboard</h1></div><div id="action-banner"></div><div id="trial-banner"></div>
    <div class="card" style="margin-top:1.5rem"><div class="card-body">
      <h2 style="font-size:1.125rem;font-weight:600;margin-bottom:.75rem">Schnellstart</h2>
      <div class="quick-grid" id="quick-grid">
        <a class="quick-link" data-nav="klassen"><div class="ql-title">Klassen verwalten</div><div class="ql-desc">Klassen anlegen und bearbeiten</div></a>
        <a class="quick-link" data-nav="praktikumsstellen"><div class="ql-title">Praktikumsstellen</div><div class="ql-desc">Kooperationspartner verwalten</div></a>
        <a class="quick-link" id="ql-planung" data-nav="planung"><div class="ql-title">Planung öffnen</div><div class="ql-desc">Praktika im Grid planen</div></a>
      </div>
    </div></div>
    <div class="card" style="margin-top:1.5rem" id="module-card"><div class="card-body">
      <h2 style="font-size:1.125rem;font-weight:600;margin-bottom:.75rem">Module</h2>
      <div id="module-grid" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(16rem,1fr));gap:1rem"></div>
    </div></div>`;

  const bannerEl = el.querySelector("#action-banner")!;
  const trialBannerEl = el.querySelector("#trial-banner")!;

  try {
    const [cls, stu, pra, settingsData] = await Promise.all([
      api.classes.list().catch(() => []),
      api.students.list().catch(() => []),
      api.practices.list().catch(() => []),
      api.settings.get().catch(() => null),
    ]);

    // Onboarding-Checkliste (hat Vorrang vor anderen Bannern)
    const onboardingState: OnboardingState = {
      onboardingCompletedAt: settingsData?.onboardingCompletedAt ?? null,
      onboardingSteps: settingsData?.onboardingSteps ?? [],
      classCount: (cls as any[]).length,
      studentCount: (stu as any[]).length,
      practiceCount: (pra as any[]).length,
    };
    if (shouldShowOnboarding(onboardingState)) {
      bannerEl.innerHTML = renderOnboardingChecklist(onboardingState);
      attachChecklistListeners(bannerEl, () => render(el));
    }

    // Banner: unverteilte Stunden (nur wenn Planung aktiv und kein Onboarding)
    else if (settingsData?.planungEnabled && settingsData?.groupPoolHours && settingsData.groupPoolHours > 0) {
      const activeDepts = settingsData.departments.filter((d: any) => d.active !== false);
      const allocated = activeDepts.reduce((s: number, d: any) => s + (d.poolHours ?? 0), 0);
      const unallocated = settingsData.groupPoolHours - allocated;
      if (activeDepts.length > 0 && unallocated > 0) {
        bannerEl.innerHTML = `
          <div style="background:#fef3c7;border:1px solid #d97706;border-radius:.5rem;padding:.875rem 1rem;color:#92400e;margin-bottom:1.25rem">
            <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:1rem">
              <div>
                <strong>Für ${settingsData.departmentGroupName} sind ${settingsData.groupPoolHours} h zur Verteilung auf die Fachbereiche der Praktischen Ausbildung vorgesehen.</strong>
                <div style="font-size:.8125rem;margin-top:.25rem;line-height:1.5">
                  Davon sind noch <strong>${unallocated} h</strong> nicht zugewiesen. Diese müssen
                  konfiguriert werden, damit die vollständigen Stundenziele gelten. Die Verteilung kannst du
                  anschließend pro Schüler individuell anpassen.
                </div>
              </div>
              <a data-nav="einstellungen" class="btn btn-sm btn-secondary"
                 style="flex-shrink:0;white-space:nowrap;align-self:center">Jetzt verteilen →</a>
            </div>
          </div>`;
      }
    }

    // Trial-Banner: Countdown anzeigen wenn Testphase aktiv
    const trialBanners: string[] = [];
    if (settingsData?.planungTrialEndsAt) {
      const trialEnd = new Date(settingsData.planungTrialEndsAt);
      const daysLeft = Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      trialBanners.push(`
        <div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:.5rem;padding:.875rem 1rem;
                    display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-bottom:.75rem">
          <span style="color:#1d4ed8;font-size:.9rem">
            ⏱ <strong>Planungsmodul – Testphase</strong>: noch ${daysLeft} Tag${daysLeft !== 1 ? "e" : ""} verfügbar
          </span>
          <a data-nav="abonnements" style="font-size:.85rem;color:#1d4ed8;text-decoration:underline;cursor:pointer;white-space:nowrap">
            Jetzt abonnieren →
          </a>
        </div>`);
    }
    if (settingsData?.schuelerPortalTrialEndsAt) {
      const trialEnd = new Date(settingsData.schuelerPortalTrialEndsAt);
      const daysLeft = Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      trialBanners.push(`
        <div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:.5rem;padding:.875rem 1rem;
                    display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-bottom:.75rem">
          <span style="color:#6d28d9;font-size:.9rem">
            ⏱ <strong>Schüler-Portal – Testphase</strong>: noch ${daysLeft} Tag${daysLeft !== 1 ? "e" : ""} verfügbar
          </span>
          <a data-nav="abonnements" style="font-size:.85rem;color:#6d28d9;text-decoration:underline;cursor:pointer;white-space:nowrap">
            Jetzt abonnieren →
          </a>
        </div>`);
    }
    if (settingsData?.stundenplanTrialEndsAt) {
      const trialEnd = new Date(settingsData.stundenplanTrialEndsAt);
      const daysLeft = Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      trialBanners.push(`
        <div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:.5rem;padding:.875rem 1rem;
                    display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-bottom:.75rem">
          <span style="color:#047857;font-size:.9rem">
            ⏱ <strong>Stundenplan – Testphase</strong>: noch ${daysLeft} Tag${daysLeft !== 1 ? "e" : ""} verfügbar
          </span>
          <a data-nav="abonnements" style="font-size:.85rem;color:#047857;text-decoration:underline;cursor:pointer;white-space:nowrap">
            Jetzt abonnieren →
          </a>
        </div>`);
    }
    if (trialBanners.length > 0) {
      trialBannerEl.innerHTML = `<div style="margin-bottom:.5rem">${trialBanners.join("")}</div>`;
    }

    // Schnellstart: Planung-Link sperren wenn nicht freigeschaltet
    const qlPlanung = el.querySelector("#ql-planung") as HTMLElement | null;
    if (qlPlanung && !settingsData?.planungEnabled) {
      qlPlanung.dataset.nav = "abonnements";
      qlPlanung.style.opacity = "0.5";
      const titleEl = qlPlanung.querySelector(".ql-title");
      if (titleEl) titleEl.textContent = "Planung 🔒";
      const descEl = qlPlanung.querySelector(".ql-desc");
      if (descEl) descEl.textContent = "Jetzt freischalten";
    }

    // Module-Karten rendern
    const moduleGrid = el.querySelector("#module-grid")!;
    const modules = [
      {
        key: "planung",
        label: "Planung",
        icon: Icons.calendarDays,
        desc: "Plane Praktika im übersichtlichen Grid und weise Schüler automatisch zu.",
        enabled: settingsData?.planungEnabled ?? false,
        activeNav: "planung",
        activeLabel: "Zur Planung →",
      },
      {
        key: "schuelerPortal",
        label: "Schüler-Portal",
        icon: Icons.academicCap,
        desc: "Schüler können ihre Praktikumseinsätze selbst einsehen und den Stundennachweis führen.",
        enabled: settingsData?.schuelerPortalEnabled ?? false,
        activeNav: "schueler",
        activeLabel: "Zugänge verwalten →",
      },
      {
        key: "stundenplan",
        label: "Stundenplan",
        icon: Icons.clipboardDocumentList,
        desc: "Plane Unterricht je Klasse mit Dozenten und Fächern und behalte die Anwesenheiten im Blick.",
        enabled: settingsData?.stundenplanEnabled ?? false,
        activeNav: "stundenplan",
        activeLabel: "Zum Stundenplan →",
      },
    ];
    moduleGrid.innerHTML = modules.map(m => {
      if (m.enabled) {
        return `<div style="border:2px solid var(--primary);border-radius:.5rem;padding:1rem;display:flex;flex-direction:column;gap:.5rem">
          <div style="display:flex;align-items:center;gap:.5rem">
            <span class="mod-icon" style="color:var(--gray-900)">${m.icon}</span>
            <strong style="font-size:1rem">${m.label}</strong>
            <span style="margin-left:auto;background:var(--primary);color:#fff;font-size:.6875rem;font-weight:600;padding:.1rem .4rem;border-radius:.25rem">Aktiv</span>
          </div>
          <div style="font-size:.8125rem;color:var(--gray-600);line-height:1.5">${m.desc}</div>
          <a class="btn btn-sm btn-primary" data-nav="${m.activeNav}" style="align-self:flex-start;margin-top:.25rem">${m.activeLabel}</a>
        </div>`;
      } else {
        return `<div style="background:var(--gray-100);border:1px solid var(--gray-200);border-radius:.5rem;padding:1rem;display:flex;flex-direction:column;gap:.5rem">
          <div style="display:flex;align-items:center;gap:.5rem">
            <span class="mod-icon" style="opacity:.5;color:var(--gray-500)">${m.icon}</span>
            <strong style="font-size:1rem;color:var(--gray-500)">${m.label} 🔒</strong>
          </div>
          <div style="font-size:.8125rem;color:var(--gray-500);line-height:1.5">${m.desc}</div>
          <a class="btn btn-sm btn-secondary" data-nav="abonnements" style="align-self:flex-start;margin-top:.25rem">Jetzt freischalten →</a>
        </div>`;
      }
    }).join("");
  } catch {}

  // Quick-Links + Banner navigieren
  el.querySelectorAll("[data-nav]").forEach(a => a.addEventListener("click", () => {
    window.location.hash = "#/" + (a as HTMLElement).dataset.nav;
  }));
}
